import React, { FC, useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Grid, Tab, Tabs } from '@mui/material'
import { Theme } from '@mui/material/styles'
import clsx from 'clsx'

interface ITabList {
  tabLabel: string
  slug: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: FC<any>
}
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IData {}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: 15,
    width: '100%',
    backGroundColor: 'transparent',
    '& .MuiBox-root': {
      padding: 0,
    },
    '& .Mui-selected': {
      opacity: 1,
      color: theme.palette.secondary.main,
    },
  },
  tabs: {
    borderBottom: '1px solid rgba(233,236,239, 0.38)',
    boxSizing: 'border-box',
    position: 'relative',
    '& button': {
      minWidth: 50,
      marginRight: 10,
      width: 'auto',
      padding: '16px 10px',
      fontSize: 14,
      fontWeight: 400,
      textTransform: 'none',
      lineHeight: 1.75,
      opacity: 0.7,
      color: theme.palette.primary.light,
      position: 'relative',
      '& span': {
        alignItems: 'flex-start',
      },
    },
  },
  tab: {
    '&:focus': {
      color: theme.palette.secondary.main,
    },
  },
  tabPanel: {
    width: '100%',
    backgroundColor: 'transparent',
    '& > div': {
      width: '100%',
    },
  },
}))

interface TabsProps {
  tabList: ITabList[]
  data?: IData
  clientId?: string | undefined
  startDate?: string | undefined
  endDate?: string | undefined
  currency?: string | undefined
  searchDebounce?: string | undefined
  getCurrentTab?: (value: string) => void | undefined
  classes?: Record<string, string>
  refreshProfile?: (value: boolean) => void
  activeTab?: string
  onChangeTab?: (key: string) => void
}

export const TabsComponent: FC<TabsProps> = ({
  data,
  tabList,
  startDate,
  endDate,
  currency,
  clientId,
  searchDebounce,
  getCurrentTab,
  activeTab,
  onChangeTab,
  refreshProfile,
  ...props
}) => {
  const classes = useStyles()
  const [value, setValue] = useState(0)

  useEffect(() => {
    // Controlled mode by key has priority
    if (activeTab) {
      const idxByKey = tabList.findIndex((it) => it.slug === activeTab)
      if (idxByKey !== -1) {
        setValue(idxByKey)
        return
      }
    }
    // Default to first tab when uncontrolled
    if (tabList.length && value !== 0) setValue(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tabList])

  const handleChange = useCallback(
    (event: React.ChangeEvent<unknown>, newValue: number) => {
      setValue(newValue)
      const newKey = tabList[newValue].slug
      if (onChangeTab) {
        onChangeTab(newKey)
      }
    },
    [tabList, onChangeTab],
  )

  const currentTabIdx = value
  const CurrentTab = tabList[currentTabIdx].component

  return (
    <div className={clsx(classes.root, props.classes?.root)}>
      <Grid container className={clsx(classes.tabs, props.classes?.tabs)}>
        <Tabs value={value} onChange={handleChange} indicatorColor="secondary">
          {tabList.map((tabItem: ITabList, idx: number) => (
            <Tab
              label={tabItem.tabLabel}
              key={idx}
              onClick={() => (getCurrentTab ? getCurrentTab(tabItem.tabLabel) : ' ')}
              className={clsx(classes.tab, props.classes?.tab)}
            />
          ))}
        </Tabs>
      </Grid>

      <Grid container className={clsx(classes.tabPanel, props.classes?.tabPanel)}>
        <CurrentTab
          data={data}
          startDate={startDate}
          endDate={endDate}
          currencyFilter={currency}
          userId={clientId}
          searchDebounce={searchDebounce}
          refreshProfile={refreshProfile}
        />
      </Grid>
    </div>
  )
}
